import React, {useEffect, useRef} from 'react';
import {IconButton, InputAdornment, Stack, TextField, Typography} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FolderOpenRoundedIcon from '@mui/icons-material/FolderOpenRounded';

/**
 * Visual display of a folder row with inline rename support.
 * Pure presentation component - no business logic.
 */
const FolderRowView = ({
                           name,
                           count,
                           isEditing,
                           draftName,
                           onDraftChange,
                           onCommitRename,
                           onCancelRename,
                           onMenuOpen,
                           onRowClick,
                           onDragOver,
                           onDragEnter,
                           onDragLeave,
                           onDrop,
                       }) => {
    const inputRef = useRef(null);

    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.select();
        }
    }, [isEditing]);

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            onCommitRename();
        } else if (event.key === 'Escape') {
            onCancelRename();
        }
    };

    return (
        <Stack
            className="folder-row"
            onClick={onRowClick}
            onDragOver={onDragOver}
            onDragEnter={onDragEnter}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            spacing={0.5}
            p={2}
            border="1px solid #cde"
            borderRadius={2}
            bgcolor="white"
            sx={{
                cursor: 'pointer',
                transition: 'box-shadow 0.2s ease',
                '&:hover': {
                    zIndex: 100,
                    boxShadow: 5,
                },
            }}
        >
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{flexGrow: 1, overflow: 'auto'}}>
                    <FolderOpenRoundedIcon fontSize="small"/>

                    {isEditing ? (
                        <TextField
                            inputRef={inputRef}
                            size="small"
                            value={draftName}
                            onChange={(e) => onDraftChange(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={handleKeyDown}
                            fullWidth
                            inputProps={{
                                maxLength: 128,
                                onBlur: onCommitRename,
                            }}
                            InputProps={{
                                endAdornment: <InputAdornment position="end"/>,
                            }}
                        />
                    ) : (
                        <Typography
                            fontWeight={600}
                            flexGrow={1}
                            sx={{
                                userSelect: 'none',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {name}
                        </Typography>
                    )}
                </Stack>

                <Typography variant="body1" color="text.secondary" sx={{mr: 1}}>
                    {count}
                </Typography>

                <IconButton
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        onMenuOpen(e);
                    }}
                    aria-label="folder options"
                >
                    <MoreVertIcon fontSize="small"/>
                </IconButton>
            </Stack>
        </Stack>
    );
};

export default FolderRowView;
